import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutGrid, List, Plus, Search, BookOpen, RotateCcw, X } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { DataTable } from "@/components/common/DataTable";
import { EntryModal } from "@/components/common/EntryModal";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ProgressBar } from "@/components/common/ProgressBar";
import { ModuleCard } from "./ModuleCard";
import { moduleFields } from "./moduleFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCrud } from "@/hooks/useCrud";
import { useResource } from "@/hooks/useResource";
import { useUser } from "@/context/UserContext";
import { OWNERS, MODULE_STATUS, productBadge } from "@/data/productOptions";

const MASCOT = "/commando-mascot-v2.webp";
const STATUS_RANK = MODULE_STATUS.reduce((a, s, i) => ({ ...a, [s.value]: i }), {});

export const ModulesPanel = ({ planets, onChange, initialStatus, initialOverdue, navKey, onClearStatus }) => {
  const navigate = useNavigate();
  const { canWrite } = useUser();
  const { items: modules, save, refetch, patch } = useCrud("modules", "Module");
  const { items: chapters } = useResource("chapters");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("cards");
  const [q, setQ] = useState("");
  const [planetF, setPlanetF] = useState("all");
  const [ownerF, setOwnerF] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [blockedF, setBlockedF] = useState("all");
  const [sort, setSort] = useState("name");
  const [showArchived, setShowArchived] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);

  // Apply deep-link status/overdue filter when navigated to (navKey bumps on each deep link).
  useEffect(() => { setStatusF(initialStatus || "all"); setOverdueOnly(!!initialOverdue); setShowArchived(false); }, [navKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearStatus = () => { setStatusF("all"); setOverdueOnly(false); onClearStatus?.(); };

  const planetOptions = planets.map((p) => ({ value: p.id, label: p.name }));
  const planetById = useMemo(() => Object.fromEntries(planets.map((p) => [p.id, p])), [planets]);
  const chapterStats = useMemo(() => {
    const map = {};
    chapters.forEach((c) => {
      map[c.module_id] = map[c.module_id] || { total: 0, completed: 0 };
      map[c.module_id].total += 1;
      if (c.status === "Complete") map[c.module_id].completed += 1;
    });
    return map;
  }, [chapters]);

  const openAdd = () => { setEditing({ owner: "Steven", status: "Not started", completion: 0, planet_id: planets[0]?.id }); setModalOpen(true); };
  const openEdit = (m) => { setEditing(m); setModalOpen(true); };

  const filtered = useMemo(() => {
    let rows = modules.filter((m) => (showArchived ? m.status === "Archived" : m.status !== "Archived"));
    if (q) { const s = q.toLowerCase(); rows = rows.filter((m) => m.name.toLowerCase().includes(s)); }
    if (planetF !== "all") rows = rows.filter((m) => m.planet_id === planetF);
    if (ownerF !== "all") rows = rows.filter((m) => m.owner === ownerF);
    if (statusF !== "all") rows = rows.filter((m) => m.status === statusF);
    if (blockedF !== "all") rows = rows.filter((m) => (blockedF === "blocked" ? !!m.current_blocker : !m.current_blocker));
    if (overdueOnly) { const td = new Date().toISOString().slice(0, 10); rows = rows.filter((m) => m.status !== "Complete" && m.status !== "Archived" && m.target_date && m.target_date < td); }
    const sorters = {
      name: (a, b) => a.name.localeCompare(b.name),
      target: (a, b) => (a.target_date || "9999").localeCompare(b.target_date || "9999"),
      progress: (a, b) => (b.completion || 0) - (a.completion || 0),
      status: (a, b) => (STATUS_RANK[a.status] || 0) - (STATUS_RANK[b.status] || 0),
    };
    rows.sort(sorters[sort] || sorters.name);
    return rows;
  }, [modules, q, planetF, ownerF, statusF, blockedF, sort, showArchived, overdueOnly]);

  const handleSave = async (v) => { await save(v); await refetch(); onChange?.(); };
  const handleRestore = async (m) => { await patch(m.id, { status: "Planned" }, "Module restored"); onChange?.(); };

  const tableColumns = [
    { key: "name", header: "Module", render: (m) => (
      <div><p className="font-semibold text-[#14245F]">{m.name}</p><p className="text-xs text-[#5A6B8C]">{planetById[m.planet_id]?.name || "Unassigned"}{m.main_character ? ` · ${m.main_character}` : ""}</p></div>
    )},
    { key: "owner", header: "Owner" },
    { key: "status", header: "Status", render: (m) => <StatusBadge status={productBadge(m.status)} label={m.status} /> },
    { key: "completion", header: "Progress", render: (m) => <div className="w-32"><ProgressBar value={m.completion} showLabel /></div> },
    { key: "target_date", header: "Target" },
    { key: "open", header: "", className: "text-right", render: (m) => (
      m.status === "Archived" ? (
        canWrite ? <Button size="sm" variant="ghost" data-testid={`restore-module-${m.id}`} onClick={() => handleRestore(m)} className="rounded-full font-semibold text-[#0EA5A4]"><RotateCcw size={14} className="mr-1" /> Restore</Button> : <span className="text-xs text-[#94A3C4]">Archived</span>
      ) : (
        <Button size="sm" variant="ghost" data-testid={`open-module-${m.id}`} onClick={() => navigate(`/product/module/${m.id}`)} className="rounded-full font-semibold text-[#0EA5A4]">Open</Button>
      )
    )},
  ];

  return (
    <SectionCard
      testid="modules-card"
      accent="#1D4ED8"
      eyebrow="Learning Adventures"
      icon={BookOpen}
      title="Modules"
      subtitle="Every learning module across all planets."
      action={
        <div className="flex items-center gap-2">
          <label className="mr-1 flex cursor-pointer items-center gap-2 text-xs font-semibold text-[#5A6B8C]">
            <Switch data-testid="modules-show-archived" checked={showArchived} onCheckedChange={setShowArchived} />
            Show archived
          </label>
          <div className="flex rounded-full border border-[#E4E9F3] p-0.5">
            <button data-testid="view-cards" onClick={() => setView("cards")} className={`flex h-7 w-7 items-center justify-center rounded-full ${view === "cards" ? "bg-[#1D4ED8] text-white" : "text-[#5A6B8C]"}`}><LayoutGrid size={15} /></button>
            <button data-testid="view-table" onClick={() => setView("table")} className={`flex h-7 w-7 items-center justify-center rounded-full ${view === "table" ? "bg-[#1D4ED8] text-white" : "text-[#5A6B8C]"}`}><List size={15} /></button>
          </div>
          {canWrite && <Button size="sm" data-testid="add-module-btn" onClick={openAdd} className="rounded-full bg-[#1D4ED8] text-white hover:bg-[#1A45C0]"><Plus size={15} /> Add Module</Button>}
        </div>
      }
    >
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search size={16} strokeWidth={2} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#5A6B8C]" />
          <Input data-testid="modules-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search modules…" className="rounded-full border-[#E4E9F3] bg-white pl-9 focus-visible:ring-[#1D4ED8]/40" />
        </div>
        <div className="flex flex-wrap gap-2">
          <FilterSelect testid="filter-planet" value={planetF} onChange={setPlanetF} label="Planet" options={planetOptions} />
          <FilterSelect testid="filter-owner" value={ownerF} onChange={setOwnerF} label="Owner" options={OWNERS} />
          <FilterSelect testid="filter-status" value={statusF} onChange={setStatusF} label="Status" options={MODULE_STATUS} />
          <FilterSelect testid="filter-blocked" value={blockedF} onChange={setBlockedF} label="Blocked" options={[{ value: "blocked", label: "Has blocker" }, { value: "clear", label: "No blocker" }]} />
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="modules-sort" className="w-auto min-w-[130px] rounded-full border-[#E4E9F3] bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Sort: Name</SelectItem>
              <SelectItem value="target">Sort: Target date</SelectItem>
              <SelectItem value="progress">Sort: Progress</SelectItem>
              <SelectItem value="status">Sort: Status</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {(statusF !== "all" || overdueOnly) && (
        <div data-testid="modules-filter-banner" className="mb-4 flex items-center justify-between rounded-xl bg-[#1D4ED8]/10 px-3 py-2 text-xs font-semibold text-[#1D4ED8]">
          <span data-testid="modules-filter-label">Filtered to {overdueOnly ? "overdue" : `“${statusF}”`} modules · {filtered.length} matching</span>
          <button data-testid="clear-module-filter" onClick={clearStatus} className="inline-flex items-center gap-1 underline hover:no-underline"><X size={13} /> Clear filter</button>
        </div>
      )}
      {filtered.length === 0 ? (
        <EmptyState image={MASCOT} title="No modules on this route yet" description="Adjust your filters, or add the first learning adventure to begin building content." actionLabel="Add module" onAction={openAdd} />
      ) : view === "cards" ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="modules-grid">
          {filtered.map((m, i) => (
            <ModuleCard key={m.id} index={i} module={m} planet={planetById[m.planet_id]} chapterStats={chapterStats[m.id]} onOpen={() => navigate(`/product/module/${m.id}`)} onRestore={m.status === "Archived" && canWrite ? () => handleRestore(m) : null} />
          ))}
        </div>
      ) : (
        <DataTable testid="modules-table" columns={tableColumns} rows={filtered} />
      )}

      <EntryModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={editing?.id ? "Edit Module" : "Add Module"}
        submitLabel={editing?.id ? "Save Changes" : "Add Module"}
        fields={moduleFields(planetOptions)}
        initialValues={editing}
        onSubmit={handleSave}
      />
    </SectionCard>
  );
};

const FilterSelect = ({ testid, value, onChange, label, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="w-auto min-w-[120px] rounded-full border-[#E4E9F3] bg-white"><SelectValue placeholder={label} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{label}: All</SelectItem>
      {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);
